import React from 'react';
import { Rect, Text } from 'react-konva';

/**
 * Stack decorator — visual affordance used by shapes that represent a
 * clustered group of records (e.g. "×12 EC2 instances of the same role"
 * in the Compass Explorer, or any future "grouped nodes" use case).
 *
 * Triggered by shape `attrs.stack = { count: N }`. The decorator
 * renders two offset shadow rectangles behind the primary card and a
 * rounded `×N` badge on top of it. The caller splits this into two
 * mount points so the badge stays above the card while the shadows
 * sit behind it.
 *
 * Intentionally domain-agnostic: callers pass `accent` (the shape's
 * primary colour) and dimensions. No compass/network imports.
 */

export interface StackShadowProps {
  count: number;
  width: number;
  height: number;
  rearFill?: string;
  frontFill?: string;
  stroke?: string;
}

/** Two offset rectangles rendered behind the primary card. */
export const StackShadow: React.FC<StackShadowProps> = ({
  count,
  width,
  height,
  rearFill = '#f1f5f9',
  frontFill = '#f8fafc',
  stroke = '#cbd5e1',
}) => {
  if (count <= 1) return null;
  return (
    <>
      <Rect
        x={8}
        y={8}
        width={width}
        height={height - 10}
        fill={rearFill}
        stroke={stroke}
        strokeWidth={1}
        cornerRadius={6}
      />
      <Rect
        x={4}
        y={4}
        width={width}
        height={height - 10}
        fill={frontFill}
        stroke={stroke}
        strokeWidth={1}
        cornerRadius={6}
      />
    </>
  );
};

export interface StackBadgeProps {
  count: number;
  width: number;
  accent: string;
}

/** `×N` pill rendered on top of the card, top-right. */
export const StackBadge: React.FC<StackBadgeProps> = ({ count, width, accent }) => {
  if (count <= 1) return null;
  const label = `×${count}`;
  const badgeW = Math.max(26, 10 + label.length * 7);
  const badgeH = 18;
  const badgeX = width - badgeW - 6;
  const badgeY = 6;
  return (
    <>
      <Rect
        x={badgeX}
        y={badgeY}
        width={badgeW}
        height={badgeH}
        fill={accent}
        cornerRadius={9}
        listening={false}
      />
      <Text
        x={badgeX}
        y={badgeY + 3}
        width={badgeW}
        height={badgeH}
        text={label}
        fontSize={11}
        fontStyle="bold"
        fontFamily="Arial, sans-serif"
        fill="#ffffff"
        align="center"
        listening={false}
      />
    </>
  );
};

/** Extract stack count from shape attrs; returns 0 when absent. */
export function readStackCount(attrs: Record<string, unknown> | null | undefined): number {
  const stack = attrs?.stack as { count?: unknown } | undefined;
  return stack && typeof stack.count === 'number' ? stack.count : 0;
}
